/**
 * @module math_processing_errors
 * @description This module contains the error handling functions for the math processing and UI language translations
 */

import type { ErrorString } from '@Shelly/cbl/src/core/errors';
import { format } from 'svelte-i18n';
import { get } from 'svelte/store';

let _format = get(format);

type MathErrors =
	| 'NO_COMPARISON_OPERATOR'
	| 'INCOMPLETE_EXPRESSION'
	| 'NO_PARAMETERS'
	| 'INVALID_EXPRESSION'
	| ErrorString;

// Type for the math error message. False is used to indicate no error.
export type MathErrorMessage = {
	message: string;
	type: 'error' | 'info';
};

/**
 * Handles math errors and returns a formatted error message.
 * 	This function is used to handle errors that occur during math processing, from UI or CBL library.
 * 	It formats the error message based on the error name and the result object.
 *  Error type is used to determine if the error is an info guidance or an error.
 *
 * @param error_name {MathErrors}  The error name to be handled
 * @param res  {any}  The result object containing the error details
 *
 * @returns  MathErrorMessage  Message object containing the error message and error/info type
 */
export const handleMathError = (error_name: MathErrors, res?: any): MathErrorMessage => {
	console.log(`[Math Transpiling] error [${error_name}]`, res);
	let message: string = '';
	let type = 'error' as MathErrorMessage['type'];

	switch (error_name) {
		case 'NO_COMPARISON_OPERATOR':
			type = 'info'; //Guide user to use comparison operators
			message = _format('scenes_error_no_math_comparison', {
				default: 'Use the available comparison operators: {operators}',
				values: { operators: res.COMPARISON_OPERATORS.join(', ') },
			});
			break;
		case 'NO_PARAMETERS':
			type = 'info';
			message = _format('scenes_error_no_math_parameters', {
				default: 'Please, select device parameters',
			});
			break;
		//CBL library errors
		case 'INVALID_PARAMS':
			message = _format('scenes_error_invalid_math_params', {
				default: 'Invalid math parameters.',
			});
			break;
		case 'UNDEFINED_VARIABLE':
			message = _format('scenes_error_undefined_math_variable', {
				default: 'Unknown Math parameter used. Please, select parameter for: "{variable}"',
				values: { variable: res?.data?.variable },
			});
			break;
		case 'INVALID_IDENTIFIER':
			let ipos = res?.data?.errorPos;
			// Get the identifier from the expression on the error position
			let identifier = res?.expression?.substring(ipos, ipos + 1);
			message = _format('scenes_error_invalid_math_identifier', {
				default: 'Invalid symbol. Expecting operator or parameter: "{identifier}"',
				values: { identifier },
			});
			break;
		case 'UNEXPECTED_SYMBOL':
			// Error occurs when parameter name starts with a number (e.g. 2A)
			let uPos = res?.data?.errorPos;
			let uNumber = res?.data?.number || res?.expression?.charAt(uPos);
			let uSymbol = res?.data?.symbol || res?.expression?.charAt(uPos + 1);

			message = _format('scenes_error_math_unexpected_symbol', {
				default: 'Parameter name cannot start with number: "{number}" before "{symbol}".',
				values: { number: uNumber, symbol: uSymbol },
			});
			break;
		case 'UNSUPPORTED_SYMBOL':
			let pos = res?.data?.errorPos;
			let symbol = res?.data?.symbol || res?.expression?.charAt(pos);
			let supported = [...res?.CALCULATION_OPERATORS, ...res?.COMPARISON_OPERATORS].join(', ');
			message = _format('scenes_error_math_unsupported_symbol', {
				default: 'Unsuported operator: "{symbol}". Supported Math operators: {operators}',
				values: { symbol: symbol, operators: supported },
			});
			break;
		case 'UNTERMINATED_STRING':
			message = _format('scenes_error_unterminated_expression_string', {
				default: 'Incomplete expression string. Make sure all quotations are closed.',
			});
			break;
		case 'LEX_ERROR':
			message = _format('scenes_error_incomplete_expression', {
				default: 'Incomplete or misspelled math expression.',
			});
			break;
		// Default error handling for the CBL library
		case 'INVALID_EXPRESSION':
		default:
			if (res.message) {
				message = _format(`scenes_error_expression_error`, {
					default: `Invalid expression: {message}`,
					values: { message: res.message },
				});
			} else {
				message = _format(`scenes_error_invalid_math_expression`, {
					default: `Invalid expression. An error occurred while processing this math expression.`,
				});
			}
			break;
	}
	return { message, type };
};
